import { DATA_SOURCES, isCovered } from '@/config/constants';
import { SpateMetricMetadata, TIKTOK_METADATA_BY_KEY } from 'utils/metrics';
import { TabButtonLink, TabList } from 'components/DataSourceTab';
import TopPostsSection, {
    DISPLAY_MODES,
} from 'components/Posts/TopPostsSection';
import { formatGrowth, formatVolume } from 'utils/helpers';
import {
    generatePath,
    useLocation,
    useParams,
    useRouteMatch,
} from 'react-router-dom';
import { useCurrentCountry, useTikTokWeeks } from 'utils/hooks';
import { useEntity } from 'utils/hooks/api';
import { DataSourceIndustriesOnlyBreadCrumbs } from 'components';
import { IndustriesPlaceholderMessage } from 'components/Placeholders';
import NoTikTokDataSection from 'components/NoTikTokDataSection';
import PageBar from 'components/PageBar';
import { PagePadding } from 'components/PagePadding';
import Stack from 'components/Stack';
import { TIKTOK_COUNTRIES } from '../../config/constants';
import { mixpanelTrack } from 'utils/app';
import styled from 'styled-components';
import { useEffect } from 'react';
import { useUserIndustriesTikTok } from 'utils/hooks/useUserIndustries';

class ColumnMetric extends SpateMetricMetadata {
    constructor(config) {
        super(config);
        this.format = (val) => val;
    }
}

const brandNameMetric = new ColumnMetric({
    key: 'brandName',
    tableHeadColumnLabel: 'Brand',
    tableColumnWidthMin: 8,
});

const cardMetrics = [
    [TIKTOK_METADATA_BY_KEY.last_week_views, true],
    [TIKTOK_METADATA_BY_KEY.total_post_views, false],
    [TIKTOK_METADATA_BY_KEY.non_cum_WoW, false],
    [brandNameMetric, true],
    [TIKTOK_METADATA_BY_KEY.create_timestamp, false],
];

const tableMetrics = [
    [TIKTOK_METADATA_BY_KEY.sentiment_score, true],
    [TIKTOK_METADATA_BY_KEY.last_week_views, true],
    [TIKTOK_METADATA_BY_KEY.total_post_views, false],
    [TIKTOK_METADATA_BY_KEY.non_cum_WoW, false],
    [brandNameMetric, true],
    [TIKTOK_METADATA_BY_KEY.create_timestamp, true],
];

function Category() {
    const { categoryId: rootCategoryId } = useParams();
    const country = useCurrentCountry();
    // Only the industry's name is needed, and this is a TikTok page.
    const categoryQuery = useEntity({
        id: rootCategoryId,
        entityType: 'categories',
        dataSource: DATA_SOURCES.TIKTOK,
        enabled: !!(rootCategoryId && country),
    });

    const { week, year } = useTikTokWeeks();

    const name = categoryQuery.data
        ? categoryQuery.data.name
        : rootCategoryId
          ? 'index'
          : null;

    useEffect(() => {
        if (name && country) {
            mixpanelTrack('Brands Top Posts', {
                name,
                countryName: country.name,
            });
        }
    }, [name, country]);

    if (!rootCategoryId) {
        return <IndustriesPlaceholderMessage />;
    }

    return (
        <TopPostsSection
            year={year}
            week={week}
            categories={rootCategoryId ? [rootCategoryId] : null}
            brands="*"
            countries={country ? [country.id] : null}
            name={name}
            displayQty={25}
            defaultDisplayMode={DISPLAY_MODES.CARDS}
            perPage={1000}
            cardMetrics={cardMetrics}
            tableMetrics={tableMetrics}
            tableName="brands-top-posts"
        />
    );
}

export const PageHeading = styled.h1`
    font-weight: ${({ theme }) => theme.fontBold};
    text-transform: capitalize;
    font-family: Montserrat;
    font-size: 18px;
    line-height: 22px;
    color: #000000;
    margin-top: 0px;
`;

export const PageSubHeading = styled.h2`
    font-weight: normal;
    font-size: 16px;
    line-height: 20px;
    color: ${({ theme }) => theme.gray_3};
    margin: 0px;
`;

const tikTokBrandMetrics = [
    { key: 'non_cum_brand_views', label: 'views', formatter: formatVolume },
    {
        key: 'non_cum_brand_views_YoY_rolling',
        label: 'YoY',
        formatter: formatGrowth,
    },
];

function TikTokBrands({
    rootCategoryId,
    generateLocation,
    metrics,
    useIndustriesHook,
}) {
    const country = useCurrentCountry();
    const industriesQuery = useIndustriesHook();
    const industries = industriesQuery.data;

    const value =
        industries && rootCategoryId
            ? industries.find(({ id }) => id === rootCategoryId)
            : undefined;

    return (
        <Stack $space="1.5rem">
            <div
                css={`
                    position: relative;
                    z-index: 1;
                `}
            >
                <DataSourceIndustriesOnlyBreadCrumbs
                    dataSource={DATA_SOURCES.TIKTOK}
                    cat1={value?.name}
                    geo={country?.text_id}
                    metrics={metrics}
                    generateLocation={generateLocation}
                    {...(rootCategoryId && {
                        id: rootCategoryId,
                        isActive: true,
                    })}
                >
                    {value ? value.name : ''}
                </DataSourceIndustriesOnlyBreadCrumbs>
            </div>
            <div css="display: flex; justify-content: center;">
                <TabList>
                    <TabButtonLink intent="primary" to={location}>
                        TikTok
                    </TabButtonLink>
                </TabList>
            </div>
            {((value?.name || !industriesQuery.isLoading) &&
                country &&
                !isCovered({
                    dataSource: DATA_SOURCES.TIKTOK,
                    cat1: value?.name,
                    geo: country?.text_id,
                }) &&
                rootCategoryId) ||
            (!rootCategoryId &&
                !TIKTOK_COUNTRIES.includes(country?.text_id)) ? (
                <NoTikTokDataSection />
            ) : (
                <Category />
            )}
        </Stack>
    );
}

function TopPosts() {
    const location = useLocation();
    const { path } = useRouteMatch();
    const { breakdown, type, categoryId } = useParams();
    const generateLocation = ({ id }) => ({
        pathname: generatePath(path, { breakdown, type, categoryId: id }),
        search: location.search,
    });

    return (
        <PagePadding className="max-w-7xl">
            <PageBar />
            <Stack $space="1.25rem">
                <TikTokBrands
                    rootCategoryId={categoryId}
                    generateLocation={generateLocation}
                    metrics={tikTokBrandMetrics}
                    useIndustriesHook={useUserIndustriesTikTok}
                />
            </Stack>
        </PagePadding>
    );
}

export default TopPosts;
